import React, { useCallback, useMemo } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPromotionCampaign, getPromotionCampaigns, updatePromotionCampaign, updatePromotionCampaignPromotions } from "../../../services/PromotionCampaign/PromotionCampaign.service";
import { TIME } from "../../../services/date.service";


export const useCampaigns = (filters = {}) => {
  return useQuery({
    queryKey: ['promotionCampaigns',"search", filters],
    queryFn: () => getPromotionCampaigns(filters),
    
  });
};

export function useCampaignsReload() {
    const queryClient = useQueryClient();

    const reload = useCallback(() => {
        return queryClient.invalidateQueries({ queryKey: ['promotionCampaigns'] });
    }, [queryClient]);
    return reload;
}

const snapshotCampaignQueries = (queryClient, campaignId) => {
  return {
    campaignId,
    previousCampaign: queryClient.getQueryData(['promotionCampaigns', campaignId]),
    previousCampaignLists: queryClient.getQueriesData({ queryKey: ['promotionCampaigns', 'search'] }),
  };
};

const restoreCampaignQueries = (queryClient, snapshot) => {
  if (!snapshot) {
    return;
  }

  queryClient.setQueryData(['promotionCampaigns', snapshot.campaignId], snapshot.previousCampaign);

  if (Array.isArray(snapshot.previousCampaignLists)) {
    for (const [key, data] of snapshot.previousCampaignLists) {
      queryClient.setQueryData(key, data);
    }
  }
};

const patchCampaignInAllQueries = (queryClient, campaignId, patchFn) => {
  queryClient.setQueryData(['promotionCampaigns', campaignId], (previous) => {
    if (!previous) {
      return previous;
    }
    return patchFn(previous);
  });

  queryClient.setQueriesData({ queryKey: ['promotionCampaigns', 'search'] }, (previous) => {
    if (!Array.isArray(previous)) {
      return previous;
    }

    return previous.map((campaign) => (
      campaign?.id === campaignId
        ? patchFn(campaign)
        : campaign
    ));
  });
};

export const useUpdateCampaignStatusMutation = () => {
  const queryClient = useQueryClient();
  const reloadCampaigns = useCampaignsReload();

  return useMutation({
    mutationFn: ({ id, status }) => updatePromotionCampaign(id, { status }),
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ['promotionCampaigns'] });

      const snapshot = snapshotCampaignQueries(queryClient, id);

      patchCampaignInAllQueries(queryClient, id, (campaign) => ({
        ...campaign,
        status,
      }));

      return snapshot;
    },
    onError: (_error, _variables, snapshot) => {
      restoreCampaignQueries(queryClient, snapshot);
    },
    onSettled: () => {
      reloadCampaigns();
    },
  });
};

export const useBulkUpdateCampaignPromotionsMutation = (campaignId, options = {}) => {
  const queryClient = useQueryClient();
  const reloadCampaigns = useCampaignsReload();

  return useMutation({
    mutationFn: ({ ids, status }) => {
      return updatePromotionCampaignPromotions(campaignId, {
        ids,
        patch: {
          status,
        },
      });
    },
    onMutate: async ({ ids, status }) => {
      await queryClient.cancelQueries({ queryKey: ['promotionCampaigns'] });

      const snapshot = snapshotCampaignQueries(queryClient, campaignId);

      patchCampaignInAllQueries(queryClient, campaignId, (campaign) => ({
        ...campaign,
        promotions: (campaign.promotions || []).map((promo) => (
          ids.includes(promo.id)
            ? { ...promo, status }
            : promo
        )),
      }));

      return snapshot;
    },
    onError: (error, variables, snapshot) => {
      restoreCampaignQueries(queryClient, snapshot);
      options.onError?.(error, variables, snapshot);
    },
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      reloadCampaigns();
      options.onSettled?.(data, error, variables, context);
    },
  });
};

export function useCampaignById(campaignId,campaigns = null) {

   const {data,isFetching,isLoading} =  useQuery({
    queryKey: ['promotionCampaigns', campaignId],
    queryFn: () => getPromotionCampaign(campaignId),
    staleTime: TIME.MINUTE_IN_MS * 5, 
    placeholderData : keepPreviousData
  });

  const backfill =  useMemo(() => {
    if (!campaigns || !campaignId) return undefined;
    return campaigns.find(c => c.id === campaignId);
  }, [campaigns, campaignId]);



  const result = useMemo(() => {
    return {
        data: data || backfill,
        isFetching,
        isLoading,
        isError: !data && !backfill,
    }
  },[data, backfill, isFetching, isLoading]);

  return result;
}
